import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import {
  FUND_COMMENT_SECTIONS,
  getPopulatedCommentSections,
} from "data/@types/SectionMapping";
import { format } from "date-fns";
import React from "react";
import { DateFormat } from "utils/date";
import { mapEntities2TitleArray } from "utils/entityTable";
import { stripHtml } from "utils/pdf/pdfGenerator";
import { formatQuarter } from "utils/quarter";

interface TableOfContentsItem {
  id: string;
  title: string;
  type: string;
  author?: string;
  date?: string;
  index: number;
}

interface CommentsPdfProps {
  tableOfContentsData: TableOfContentsItem[];
  commentsData: any[];
  commentCount: number;
  styles: ReturnType<typeof StyleSheet.create>;
}

export const CommentsPdf: React.FC<CommentsPdfProps> = ({
  tableOfContentsData,
  commentsData,
  commentCount,
  styles,
}) => {
  return (
    <Document>
      {/* Table of Contents Page */}
      <Page size="A4" style={styles.tableOfContentsPage}>
        <Text style={styles.tableOfContentsTitle}>Table of Contents</Text>
        <Text
          style={{
            fontSize: 10,
            color: "#616161",
            marginBottom: 20,
            textAlign: "center",
          }}
        >
          {commentCount} comment{commentCount > 1 ? "s" : ""}
        </Text>
        {tableOfContentsData.map((item) => (
          <View key={item.id} style={styles.tableOfContentsItem}>
            <View style={styles.tableOfContentsItemHeader}>
              <Text style={styles.tableOfContentsLink}>{item.title}</Text>
              <Text style={styles.tableOfContentsPageNumber}>
                #{item.index}
              </Text>
            </View>
            <View style={{ flexDirection: "row", marginTop: 4 }}>
              {item.type && (
                <Text style={styles.tableOfContentsMetadata}>
                  Type: {item.type}
                </Text>
              )}
              {item.author && (
                <Text
                  style={[styles.tableOfContentsMetadata, { marginLeft: 10 }]}
                >
                  • Author: {item.author}
                </Text>
              )}
              {item.date && (
                <Text
                  style={[styles.tableOfContentsMetadata, { marginLeft: 10 }]}
                >
                  • Date: {format(item.date, DateFormat.DATE)}
                </Text>
              )}
            </View>
          </View>
        ))}

        {/* Page Number Footer */}
        <Text
          style={styles.pageFooterWithPagination}
          render={({ pageNumber }) => `${pageNumber}`}
          fixed
        />
      </Page>

      {/* Comment Pages */}
      {commentsData.map((data, idx) => {
        const comment = data.commentDetails;
        const tags = data.tags || [];
        const entities = data?.commentDetails?.entities;

        // Build title using the same logic as MultipleEntityHeaderDetails
        let titleText = comment.customTitle;
        if (!titleText && entities) {
          const entityTitles = mapEntities2TitleArray(entities);
          titleText = entityTitles.join(", ");
        }

        return (
          <Page key={comment.id} size="A4" style={styles.page}>
            {/* Anchor for navigation */}
            <Text id={`comment-${idx}`} style={{ height: 0 }}></Text>

            {/* Header Section */}
            <View style={styles.header}>
              {titleText && <Text style={styles.title}>{titleText}</Text>}

              {/* Metadata Rows */}
              <View style={styles.metadataRow}>
                <Text style={styles.metadataLabel}>Type:</Text>
                <Text style={styles.metadataValue}>
                  {comment.commentType || "N/A"}
                </Text>
              </View>

              {comment.author?.name && (
                <View style={styles.metadataRow}>
                  <Text style={styles.metadataLabel}>Author:</Text>
                  <Text style={styles.metadataValue}>
                    {comment.author.name}
                  </Text>
                </View>
              )}

              {comment.commentDate && (
                <View style={styles.metadataRow}>
                  <Text style={styles.metadataLabel}>Date:</Text>
                  <Text style={styles.metadataValue}>
                    {format(comment.commentDate, DateFormat.DATE)}
                  </Text>
                </View>
              )}

              {formatQuarter(comment.quarter) && (
                <View style={styles.metadataRow}>
                  <Text style={styles.metadataLabel}>Quarter:</Text>
                  <Text style={styles.metadataValue}>
                    {formatQuarter(comment.quarter)}
                  </Text>
                </View>
              )}

              {comment.firm?.longName && (
                <View style={styles.metadataRow}>
                  <Text style={styles.metadataLabel}>Firm:</Text>
                  <Text style={styles.metadataValue}>
                    {comment.firm.longName}
                  </Text>
                </View>
              )}

              {/* Entities */}
              {comment.entities && (
                <View style={styles.entitiesSection}>
                  <Text style={styles.metadataLabel}>Entities:</Text>
                  {comment.entities.Firm?.map((entity: any) => (
                    <Text
                      key={entity.id || entity.uuid || entity.longName}
                      style={styles.entityItem}
                    >
                      • [Firm] {entity.longName}
                    </Text>
                  ))}
                  {comment.entities.Product?.map((entity: any) => (
                    <Text
                      key={entity.id || entity.uuid || entity.longName}
                      style={styles.entityItem}
                    >
                      • [Product] {entity.longName}
                    </Text>
                  ))}
                  {comment.entities.Strategy?.map((entity: any) => (
                    <Text
                      key={entity.id || entity.uuid || entity.longName}
                      style={styles.entityItem}
                    >
                      • [Strategy] {entity.longName}
                    </Text>
                  ))}
                </View>
              )}
            </View>

            {/* Tags Section */}
            {tags.length > 0 && (
              <View style={styles.tagsSection}>
                <Text style={styles.sectionLabel}>Tags</Text>
                <View style={styles.tagsContainer}>
                  {tags.map((tag: any) => (
                    <Text key={tag.id || tag.text} style={styles.tag}>
                      {tag.text}
                    </Text>
                  ))}
                </View>
              </View>
            )}

            {/* AI Summary Section */}
            {comment.aiSummaryInternal && (
              <View style={styles.aiSummarySection}>
                <Text style={styles.aiSummaryTitle}>AI Summary</Text>
                <Text style={styles.aiSummaryText}>
                  {stripHtml(comment.aiSummaryInternal)}
                </Text>
              </View>
            )}

            {/* Comment Content Section */}
            {comment.sections &&
              Object.keys(comment.sections).length > 0 &&
              (() => {
                const { internal, external } = getPopulatedCommentSections(
                  comment.sections,
                  comment.commentType,
                );
                const allSections = [...internal, ...external];

                return allSections.length > 0 ? (
                  <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Comment Content</Text>
                    {internal.length > 0 && (
                      <View style={styles.restrictedContainer}>
                        <Text style={styles.sectionLabel}>Restricted</Text>
                        {internal.map((section: any) => {
                          const content = stripHtml(section.content || "");
                          return content ? (
                            <View key={section.field}>
                              <Text style={styles.subsectionTitle}>
                                {section.title}
                              </Text>
                              <View style={styles.contentBox}>
                                <Text style={styles.text}>{content}</Text>
                              </View>
                            </View>
                          ) : null;
                        })}
                      </View>
                    )}
                    {external.length > 0 && (
                      <View style={styles.clientReadyContainer}>
                        <Text style={styles.sectionLabel}>Published</Text>
                        {external.map((section: any) => {
                          const content = stripHtml(section.content || "");
                          return content ? (
                            <View key={section.field}>
                              <Text style={styles.subsectionTitle}>
                                {section.title}
                              </Text>
                              <View style={styles.contentBox}>
                                <Text style={styles.text}>{content}</Text>
                              </View>
                            </View>
                          ) : null;
                        })}
                      </View>
                    )}
                  </View>
                ) : null;
              })()}

            {/* Fund Comments Section */}
            {comment.fundComments && comment.fundComments.length > 0 && (
              <View style={styles.fundCommentsSection}>
                <Text style={styles.sectionLabel}>Fund Specific (PI Only)</Text>
                {comment.fundComments.map((fundComment: any) => {
                  // Filter sections using same logic as FundInternalExternalDetails
                  const filteredSections = FUND_COMMENT_SECTIONS.map(
                    (section) => {
                      if (
                        section.internalField &&
                        fundComment.sections?.[section.internalField]?.content
                      ) {
                        return {
                          title: section.title,
                          content:
                            fundComment.sections[section.internalField].content,
                        };
                      } else if (
                        section.externalField &&
                        fundComment.sections?.[section.externalField]?.content
                      ) {
                        return {
                          title: section.title,
                          content:
                            fundComment.sections[section.externalField].content,
                        };
                      }
                      return null;
                    },
                  ).filter(Boolean);

                  return (
                    <View key={fundComment.id} style={styles.fundCommentBox}>
                      <Text style={styles.fundCommentTitle}>
                        {fundComment.fund?.longName || "Unknown Fund"}
                      </Text>
                      {filteredSections.map((section: any) => {
                        const content = stripHtml(section.content || "");
                        return content ? (
                          <View
                            key={section.title}
                            style={styles.fundCommentContent}
                          >
                            <Text style={styles.fundCommentSectionTitle}>
                              {section.title}
                            </Text>
                            <Text style={styles.text}>{content}</Text>
                          </View>
                        ) : null;
                      })}
                    </View>
                  );
                })}
              </View>
            )}

            {/* Page Number Footer */}
            <Text
              style={styles.pageFooterWithPagination}
              render={({ pageNumber, totalPages }) =>
                `${pageNumber} / ${totalPages}`
              }
              fixed
            />
          </Page>
        );
      })}
    </Document>
  );
};
